/**
 * Task detail overlay navigation contract.
 *
 * The task drawer is a non-navigating overlay driven by the `?task=` search
 * param. Closing it strips ONLY `task`, leaving every other search key
 * (`board`, `view`, …) untouched — so closing from any origin (board / list /
 * backlog / overview / activity) returns to that exact view. The underlying
 * view's local React state (filters, selected sprint, selection mode) never
 * leaves the component, so it survives open/close automatically.
 */
export function closeTaskSearch(prev: Record<string, unknown>): Record<string, unknown> {
  const rest = { ...prev };
  delete rest.task;
  return rest;
}

/**
 * Opens a task in that same drawer: one search key changes and every other one
 * stays, so the board underneath keeps its view, its filters and its scroll.
 *
 * `board` is left as it was, on purpose. It names the view BEHIND the drawer,
 * not the task inside it, and that is what closing returns to. It is also not
 * safe to treat as the task's board: a parent and a subtask can end up on
 * different boards, because `moveToBoard` detaches only the moved task's own
 * parent link — its own comment in `tasks.service.ts` records that the reverse
 * relation is not cascaded. The drawer therefore binds its board-scoped
 * controls to the task's `boardId` rather than to this param. [HW-819]
 */
export function openTaskSearch<T extends Record<string, unknown>>(
  prev: T,
  key: string,
): T & { task: string } {
  return { ...prev, task: key };
}
